import { getLogger } from "@core/logging/logger";
import { AuthService, HandleNewDeviceCallback, RegistrationUserAttributes } from "@domain/authentication/auth-service";
import {
	CodeMismatchError,
	ExpiredCodeError,
	IncorrectCredentialsError,
	LimitExceededCodeError,
	NewPasswordNeededError,
	NotAuthorizedCodeError,
	ResetPasswordCodeError,
	SendLimitExceededError,
	TemporaryPasswordError,
	TooManyAttemptsError,
	UsernameExistsCodeError,
	UserNotConfirmedError,
	UserNotDefinedError,
	UserNotFoundError,
} from "@domain/authentication/authentication-errors";
import {
	CognitoError,
	CognitoErrorCode,
	CognitoErrorMessage,
	isCognitoError,
} from "@domain/authentication/cognito/cognito-error";
import { CognitoMultiInfra } from "@domain/authentication/cognito/cognito-multi-infra";
import { CognitoServiceWrapper } from "@domain/authentication/cognito/cognito-service-wrapper";
import { CognitoUserPoolProvider } from "@domain/authentication/cognito/cognito-user-pool-provider";
import {
	createCognitoUserAttributes,
	getRegistrationUserAttributes,
} from "@domain/authentication/cognito/registration-user-attributes";
import { NetworkActionsErrorsManager } from "@domain/network/network-actions-errors-manager";
import { NoNetworkError } from "@domain/network/network-errors";
import { isNil, isNotNil } from "@utils/assert";
import { CognitoIdToken, CognitoUser, CognitoUserAttribute, CognitoUserSession } from "amazon-cognito-identity-js";
import { observable } from "micro-observables";
import { Signal } from "micro-signals";

const SEC_TO_MILLISEC = 1000;

export class CognitoAuthService implements AuthService {
	private readonly _idToken = observable<CognitoIdToken | null>(null);
	private readonly _cognitoUser = observable<CognitoUser | null>(null);
	private readonly _cognitoUserAttributes = observable<CognitoUserAttribute[]>([]);

	private readonly logger = getLogger("🧠 [CognitoAuthService]");

	onSessionExpired = new Signal<void>();

	constructor(
		private readonly cognitoServiceWrapper: CognitoServiceWrapper,
		private readonly userPoolProvider: CognitoUserPoolProvider,
		private readonly networkActionsErrorsManager: NetworkActionsErrorsManager,
		private readonly cognitoMultiInfra: CognitoMultiInfra,
	) {}

	async init(): Promise<void> {
		const userPool = this.userPoolProvider.getUnsafePool();
		if (isNil(userPool)) {
			this.logger.info("No user pool defined yet, skip session restoration");
			return;
		}

		try {
			const currentUser = userPool.getCurrentUser();
			if (isNotNil(currentUser)) {
				const session = await this.cognitoServiceWrapper.getSession(currentUser);
				this._idToken.set(session.getIdToken());
				await this.updateUser(currentUser);
			}
		} catch (error) {
			this.logger.error("Refresh user failed", error);
			if (isCognitoError(error) && error.code === CognitoErrorCode.NotAuthorizedException) {
				this.onSessionExpired.dispatch();
			}
		}
	}

	async isEmailAvailable(email: string): Promise<boolean> {
		return await this.cognitoMultiInfra.multipleInfraIsEmailAvailable(email);
	}

	// eslint-disable-next-line sonarjs/cognitive-complexity
	async loginEmail(email: string, password: string, onNewDevice: HandleNewDeviceCallback): Promise<void> {
		try {
			const { cognitoUser, cognitoUserSession } = await this.cognitoMultiInfra.multipleInfraLoginEmail(
				email,
				password,
				this.updateUser.bind(this),
			);
			await this.updateUser(cognitoUser);

			this._idToken.set(cognitoUserSession.getIdToken());

			const newDeviceUsed = this.getUserAttribute(CognitoAttributes.NewDevice) === "1";
			if (newDeviceUsed) {
				const deviceKey = cognitoUserSession.getAccessToken().payload.device_key as string;
				onNewDevice(deviceKey);
			}
		} catch (error) {
			let managedError;
			if (isCognitoError(error)) {
				// report Too many attempts as specific message
				if (
					error.code === CognitoErrorCode.NotAuthorizedException &&
					error.message === CognitoErrorMessage.PasswordAttemptsExceeded
				) {
					managedError = new TooManyAttemptsError(error.message);

					// Invalid login: report same error for user not found or invalid credentials
				} else if (
					error.code === CognitoErrorCode.NotAuthorizedException ||
					error.code === CognitoErrorCode.UserNotFoundException
				) {
					managedError = new IncorrectCredentialsError("Incorrect username or password.");

					// User has not confirmed email
				} else if (error.code === CognitoErrorCode.UserNotConfirmedException) {
					managedError = new UserNotConfirmedError(error.message);
				}
			} else if (error instanceof NewPasswordNeededError) {
				managedError = error;
			}

			if (isNil(managedError)) {
				this.reportCognitoError(error);
			}
			throw managedError ?? error;
		}
	}

	// eslint-disable-next-line sonarjs/cognitive-complexity
	async forgotPassword(email: string): Promise<void> {
		try {
			await this.cognitoMultiInfra.multipleInfraForgotPassword(email);
		} catch (error) {
			this.logger.debug("Forgot password failed", error);
			if (isCognitoError(error)) {
				if (error.code === CognitoErrorCode.UserNotFoundException) {
					throw new UserNotFoundError();
				}
				// report Too many attempts as specific message
				if (error.code === CognitoErrorCode.LimitExceededException) {
					throw new SendLimitExceededError(error.message);
				}
				if (error.code === CognitoErrorCode.NotAuthorizedException) {
					throw new TemporaryPasswordError(error.message);
				}
				if (error.code === CognitoErrorCode.InvalidParameterException && error.message.includes("verified email")) {
					throw new UserNotConfirmedError(error.message);
				}
			}
			this.networkActionsErrorsManager.reportError(error);
			throw error;
		}
	}

	async newPassword(email: string, resetToken: string, newPassword: string): Promise<void> {
		return new Promise((resolve, reject) => {
			const userPool = this.userPoolProvider.getPool();
			const userData = {
				Username: email,
				Pool: userPool,
			};
			const cognitoUser = new CognitoUser(userData);
			cognitoUser.confirmPassword(resetToken, newPassword, {
				onSuccess: () => {
					resolve();
				},

				onFailure: err => {
					this.logger.debug("New password failed : " + err.name + "\n" + err.message || JSON.stringify(err));
					if (err.name === "CodeMismatchException") {
						reject(new ResetPasswordCodeError(err.message));
					} else if (err.name === "ExpiredCodeException") {
						reject(new ExpiredCodeError(err.message));
					} else {
						this.networkActionsErrorsManager.reportError(err);
						reject(err);
					}
				},
			});
		});
	}

	isAuthenticated(): boolean {
		return !!this._cognitoUser.get();
	}

	async getToken(): Promise<string | undefined> {
		const token = this._idToken.get();
		if (token && token.getExpiration() * SEC_TO_MILLISEC < Date.now()) {
			await this.refreshToken();
		}
		return this._idToken.get()?.getJwtToken();
	}

	private async refreshToken(): Promise<void> {
		return new Promise((resolve, reject) => {
			const currentUser = this._cognitoUser.get();
			if (currentUser) {
				currentUser.getSession((error: Error | null, session: CognitoUserSession | null) => {
					if (error) {
						this.logger.warn("Error getting user session : " + JSON.stringify(error));
						reject(error);
					} else if (session) {
						const refreshToken = session.getRefreshToken();
						currentUser.refreshSession(refreshToken, (error2, newSession: CognitoUserSession) => {
							if (error2) {
								this.logger.warn("Error refreshing session : " + JSON.stringify(error2));
								reject(error2);
							} else {
								this._idToken.set(newSession.getIdToken());
								resolve();
							}
						});
					}
				});
			} else {
				reject(new UserNotDefinedError("user not defined when refreshing token"));
			}
		});
	}

	private updateUser(user: CognitoUser | null): Promise<void> {
		return new Promise(resolve => {
			this._cognitoUser.set(user);
			if (user) {
				user.getUserAttributes((error, result) => {
					if (!error && result) {
						this._cognitoUserAttributes.set(result);
					}
					resolve();
				});
			} else {
				this._cognitoUserAttributes.set([]);
				resolve();
			}
		});
	}

	private getUserAttribute(name: string): string | undefined {
		return this._cognitoUserAttributes.get().find(attribute => attribute.Name === name)?.Value;
	}

	async logout(): Promise<void> {
		this._cognitoUser.get()?.signOut();
		await this.updateUser(null);
		this._idToken.set(null);
	}

	private async authOnSuccess(
		user: CognitoUser,
		result: CognitoUserSession,
		resolve: () => void,
		onNewDevice: HandleNewDeviceCallback,
	) {
		await this.updateUser(user);
		this._idToken.set(result.getIdToken());

		const newDeviceUsed = this.getUserAttribute(CognitoAttributes.NewDevice) === "1";

		if (newDeviceUsed) {
			const deviceKey = result.getAccessToken().payload["device_key"] as string;
			onNewDevice(deviceKey);
		}
		resolve();
	}

	private async authOnFailure(error: CognitoError, reject: (reason: Error | CognitoError) => void) {
		this.logger.warn("Authentication failed : " + (error.message || JSON.stringify(error)));

		switch (error.code) {
			case "UserNotConfirmedException":
				reject(new UserNotConfirmedError(error.message));
				break;

			case "NotAuthorizedException": {
				if (error.message === CognitoErrorMessage.PasswordAttemptsExceeded) {
					reject(new TooManyAttemptsError(error.message));
				} else {
					reject(new IncorrectCredentialsError(error.message));
				}
				break;
			}

			case "UserNotFoundException":
				reject(new UserNotFoundError("The user doesn't exist"));
				break;

			default:
				this.networkActionsErrorsManager.reportError(error);
				reject(error);
				break;
		}
	}

	completeNewPasswordChallenge(newPassword: string, onNewDevice: HandleNewDeviceCallback): Promise<void> {
		return new Promise((resolve, reject) => {
			const currentUser = this._cognitoUser.get();
			if (isNotNil(currentUser)) {
				currentUser.completeNewPasswordChallenge(newPassword, null, {
					onSuccess: result => this.authOnSuccess(currentUser, result, resolve, onNewDevice),
					onFailure: (error: CognitoError) => this.authOnFailure(error, reject),
				});
			} else {
				reject(new UserNotDefinedError("user not defined when completing NewPasswordChallenge"));
			}
		});
	}

	async changePassword(currentPassword: string, newPassword: string): Promise<void> {
		const currentUser = this._cognitoUser.get();
		if (!currentUser) {
			throw new UserNotDefinedError("user not defined when changing password");
		}
		try {
			await this.cognitoServiceWrapper.changePassword(currentUser, currentPassword, newPassword);
		} catch (error) {
			if (isCognitoError(error) && error.code === CognitoErrorCode.NotAuthorizedException) {
				throw new NotAuthorizedCodeError(error.message);
			}
			this.reportCognitoError(error);
			throw error;
		}
	}

	async validateSignUpConfirmationCode(code: string, email: string): Promise<void> {
		this.logger.debug("Cognito validation");
		return new Promise((resolve, reject) => {
			const userPool = this.userPoolProvider.getPool();
			const currentUser = new CognitoUser({ Username: email, Pool: userPool });
			currentUser.confirmRegistration(code, true, async error => {
				if (isNil(error)) {
					resolve();
				} else {
					this.handleCognitoErrorCases(reject, resolve, error);
				}
			});
		});
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private handleCognitoErrorCases(reject: (reason?: any) => void, resolve: () => void, error: any): void {
		this.logger.error("Error confirm registration", error);
		if (isCognitoError(error)) {
			switch (error.code) {
				case CognitoErrorCode.ExpiredCodeException:
					reject(new ExpiredCodeError(error.message));
					break;

				case CognitoErrorCode.NotAuthorizedException:
					// case user is already confirmed. skip confirmation step and continue signup process (access token & backend registration)
					if (error.message.includes(CognitoErrorMessage.AlreadyConfirmed)) {
						resolve();
					} else {
						reject(new NotAuthorizedCodeError(error.message));
					}
					break;

				case CognitoErrorCode.CodeMismatchException:
					reject(new CodeMismatchError(error.message));
					break;

				default:
					this.reportCognitoError(error);
					reject(error);
			}
		} else {
			this.reportCognitoError(error);
			reject(error);
		}
	}

	private reportCognitoError(error: unknown) {
		this.logger.error("reporting a cognito error", error);
		this.networkActionsErrorsManager.reportError(
			isCognitoError(error) && error.code === CognitoErrorCode.NetworkErrorException
				? new NoNetworkError("Cognito no network")
				: error,
		);
	}

	async signUpEmail(email: string, password: string, userAttributes: RegistrationUserAttributes): Promise<void> {
		const cognitoUserAttributes = createCognitoUserAttributes({ ...userAttributes, email });
		return new Promise((resolve, reject) => {
			const userPool = this.userPoolProvider.getPool();
			userPool.signUp(email, password, cognitoUserAttributes, [], async (error, result) => {
				if (error) {
					this.logger.error("Signup Error : ", error);
					if (isCognitoError(error) && error.code === CognitoErrorCode.LimitExceededException) {
						reject(new LimitExceededCodeError(error.message));
					} else if (isCognitoError(error) && error.code === CognitoErrorCode.UsernameExistsException) {
						reject(new UsernameExistsCodeError(error.message));
					} else {
						this.reportCognitoError(error);
						reject(error);
					}
				} else if (!result) {
					this.logger.error("signUp error: no user");
					const nullUserError = new Error("signUp error: no user");
					this.reportCognitoError(nullUserError);
					reject(nullUserError);
				} else {
					await this.updateUser(result.user);
					resolve();
				}
			});
		});
	}

	async resendSignUpValidationCode(email: string): Promise<void> {
		const userPool = this.userPoolProvider.getPool();
		const currentUser = new CognitoUser({ Username: email, Pool: userPool });
		try {
			await this.cognitoServiceWrapper.resendConfirmationCode(currentUser);
			await this.updateUser(currentUser);
		} catch (error) {
			this.logger.error("Error resending confirmation code", error);

			if (isCognitoError(error) && error.code === CognitoErrorCode.LimitExceededException) {
				throw new LimitExceededCodeError(error.message);
			} else {
				this.reportCognitoError(error);
				throw error;
			}
		}
	}

	getRegistrationUserAttributes(): RegistrationUserAttributes | null {
		const userAttributes = this._cognitoUserAttributes.get();
		return getRegistrationUserAttributes(userAttributes);
	}
}

enum CognitoAttributes {
	Email = "email",
	Locale = "locale",
	NewDevice = "custom:new_device",
}
